import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Whether Turnstile is enabled in the frontend.
 * When false, the widget does not render and no token is required.
 * Defaults to true unless explicitly set to 'false'.
 */
export const TURNSTILE_ENABLED =
    process.env.REACT_APP_TURNSTILE_ENABLED !== 'false';

/**
 * Cloudflare Turnstile site key.
 * Defaults to the Cloudflare "always passes" test key for localhost development.
 * Override via REACT_APP_TURNSTILE_SITE_KEY in .env.local / .env.production.
 */
const TURNSTILE_SITE_KEY =
    process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

interface TurnstileWidgetProps {
    /** Called when verification succeeds — receives the single-use token. */
    onVerify: (token: string) => void;
    /** Called when the token expires (optional). */
    onExpire?: () => void;
    /** Called on widget error (optional). */
    onError?: () => void;
    /**
     * Rendering mode: 'managed' shows the checkbox, 'invisible' auto-challenges.
     * Default: 'managed' — lets the widget decide whether to show a challenge.
     */
    appearance?: 'always' | 'execute' | 'interaction-only';
}

/**
 * Reusable Cloudflare Turnstile widget.
 *
 * Loads the Turnstile script once, renders the widget into a div, and calls
 * `onVerify` with the single-use token when the challenge passes.
 *
 * Usage:
 *   const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
 *   const turnstileRef = useRef<TurnstileHandle>(null);
 *   <TurnstileWidget ref={turnstileRef} onVerify={setTurnstileToken} />
 *   // After submit: turnstileRef.current?.reset();
 */
export interface TurnstileHandle {
    /** Reset the widget (e.g. after a failed submit so the user can retry). */
    reset: () => void;
}

/** Inner component that actually renders the Turnstile challenge. */
const TurnstileWidgetInner = React.forwardRef<TurnstileHandle, TurnstileWidgetProps>(
    ({ onVerify, onExpire, onError, appearance }, ref) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const widgetIdRef = useRef<string | null>(null);
        const [scriptLoaded, setScriptLoaded] = useState(
            typeof window !== 'undefined' && !!(window as any).turnstile
        );

        // Load Turnstile script once globally
        useEffect(() => {
            if ((window as any).turnstile) {
                setScriptLoaded(true);
                return;
            }
            const existing = document.querySelector(
                'script[src*="challenges.cloudflare.com/turnstile"]'
            );
            if (existing) {
                existing.addEventListener('load', () => setScriptLoaded(true));
                return;
            }
            const script = document.createElement('script');
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
            script.async = true;
            script.defer = true;
            script.onload = () => setScriptLoaded(true);
            document.head.appendChild(script);
        }, []);

        // Render widget once script is loaded
        useEffect(() => {
            if (!scriptLoaded || !containerRef.current) return;
            const turnstile = (window as any).turnstile;
            if (!turnstile) return;

            // Clean up previous widget if any
            if (widgetIdRef.current !== null) {
                try { turnstile.remove(widgetIdRef.current); } catch { /* ignore */ }
            }

            widgetIdRef.current = turnstile.render(containerRef.current, {
                sitekey: TURNSTILE_SITE_KEY,
                callback: onVerify,
                'expired-callback': onExpire,
                'error-callback': onError,
                appearance: appearance || 'interaction-only',
                size: 'flexible',
            });

            return () => {
                if (widgetIdRef.current !== null) {
                    try { turnstile.remove(widgetIdRef.current); } catch { /* ignore */ }
                    widgetIdRef.current = null;
                }
            };
            // Re-render only when script loads — callbacks are stable refs
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [scriptLoaded]);

        const reset = useCallback(() => {
            const turnstile = (window as any).turnstile;
            if (turnstile && widgetIdRef.current !== null) {
                turnstile.reset(widgetIdRef.current);
            }
        }, []);

        React.useImperativeHandle(ref, () => ({ reset }), [reset]);

        return <div ref={containerRef} style={{ marginBottom: 8 }} />;
    }
);

TurnstileWidgetInner.displayName = 'TurnstileWidgetInner';

/** No-op stub when Turnstile is disabled — renders nothing. */
const TurnstileWidgetDisabled = React.forwardRef<TurnstileHandle, TurnstileWidgetProps>(
    (_props, ref) => {
        React.useImperativeHandle(ref, () => ({ reset: () => {} }), []);
        return null;
    }
);

TurnstileWidgetDisabled.displayName = 'TurnstileWidgetDisabled';

/**
 * Exported component — delegates to inner or disabled stub based on
 * REACT_APP_TURNSTILE_ENABLED. The decision is made at module load time
 * (env vars don't change at runtime), so hook order is always stable.
 */
const TurnstileWidget = TURNSTILE_ENABLED ? TurnstileWidgetInner : TurnstileWidgetDisabled;

export default TurnstileWidget;
